import React, { useState, useEffect } from 'react';
import './Contact.css';

const Contact: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    serviceType: 'belsofestes'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          serviceType: 'belsofestes'
        });
      } else {
        const errorData = await response.json();
        setSubmitStatus('error');
        console.error('Contact form error:', errorData);
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const heroImage = "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop"; // Minimalist interior

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="contact-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="hero-overlay"></div>
        <div className="hero-content fade-in-up">
          <h1 className="hero-title">Kapcsolat</h1>
          <p className="hero-subtitle">Kérjen ajánlatot • Egyeztessen időpontot</p>
        </div>
      </section>

      <div className="container">
        <div className="contact-layout">
          {/* Contact Info Side */}
          <div className="contact-info-side fade-in-left">
            <h2 className="section-title-small">Elérhetőségek</h2>
            <p className="contact-desc">
              Készen áll otthona megújítására? Keressen bizalommal az alábbi elérhetőségeken, 
              vagy töltse ki az űrlapot, és hamarosan felveszem Önnel a kapcsolatot.
            </p>

            <div className="info-cards">
              <div className="info-card">
                <div className="icon">📍</div>
                <div>
                  <h3>Helyszín</h3>
                  <p>Győr és környéke</p>
                </div>
              </div>
              <div className="info-card">
                <div className="icon">📞</div>
                <div>
                  <h3>Telefon</h3>
                  <a href="tel:+36309869878" className="contact-link">+36 30 986 9878</a>
                </div>
              </div>
              <div className="info-card">
                <div className="icon">✉️</div>
                <div>
                  <h3>Email</h3>
                  <a href="mailto:info@lakicsfesto.com" className="contact-link">info@lakicsfesto.com</a>
                </div>
              </div>
            </div>

            <div className="hours-card">
              <h3>Nyitvatartás</h3>
              <div className="hours-row">
                <span>Hétfő - Péntek</span>
                <span className="time">08:00 - 18:00</span>
              </div>
              <div className="hours-row">
                <span>Szombat</span>
                <span className="time">08:00 - 14:00</span>
              </div>
              <div className="hours-row">
                <span>Vasárnap</span>
                <span className="time">Zárva</span>
              </div>
            </div>
          </div>

          {/* Form Side */}
          <div className="contact-form-side fade-in-right">
            <div className="form-wrapper">
              <h2 className="section-title-small">Üzenet küldése</h2>
              
              {submitStatus === 'success' && (
                <div className="status-message success">
                  Köszönjük megkeresését! Hamarosan válaszolunk.
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="status-message error">
                  Hiba történt. Kérjük próbálja újra később.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Név</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Az Ön neve"
                  />
                </div>

                <div className="form-group">
                  <label>Email cím</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="pelda@email.hu"
                  />
                </div>

                <div className="form-group">
                  <label>Telefonszám</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+36 30 123 4567"
                  />
                </div>

                <div className="form-group">
                  <label>Érdeklődés tárgya</label>
                  <select
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleChange}
                  >
                    <option value="belsofestes">Belső festés</option>
                    <option value="dekorativfestes">Dekoratív festés</option>
                    <option value="felujitas">Teljes felújítás</option>
                    <option value="egyeb">Egyéb</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Üzenet</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Írja le röviden miben segíthetek..."
                  />
                </div>

                <button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Küldés folyamatban...' : 'Ajánlatkérés Küldése'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
